import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { cn } from 'components/UI/cn'
import { Select } from 'components/UI/Select/Select'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, ImportData, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'
import { Tabs } from 'components/FbUI/Tabs'
import {
  setMatchedChainByHash,
  setMatchedDoorByHash,
  setAsCustomCompany,
} from '../importDataTransforms'
import { OppTypeBadge, getOppTypeLabel } from './OppTypeBadge'

/** Fields to display in the "Your Input" column - company_match and primary_key types */
type MappedField = {
  mapped: string
  original: string
  label: string
}

function clearCustomCompanyByHash(
  data: ImportData,
  companyHash: string
): ImportData {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row[InternalColumns.COMPANY_HASH] === companyHash
        ? {
            ...row,
            [InternalColumns.IS_CUSTOM_COMPANY]: false,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: null,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: null,
          }
        : row
    ),
  }
}

export function ImportWizardReviewPendingStep({
  show,
  data,
}: {
  show: boolean
  data: CSVRow[]
}) {
  const viewModel = useImporterViewModel()
  const [cursor, setCursor] = useState(0)

  // Reset cursor when data changes (e.g., after deselecting in step 1)
  useEffect(() => {
    if (cursor >= data.length && data.length > 0) {
      setCursor(data.length - 1)
    } else if (data.length === 0) {
      setCursor(0)
    }
  }, [data.length, cursor])

  function handleNext() {
    if (cursor + 1 >= data.length) {
      return
    }
    setCursor(cursor + 1)
  }

  function handlePrev() {
    if (cursor - 1 < 0) {
      return
    }
    setCursor(cursor - 1)
  }

  // Handler to clear selection for current company
  const handleClearSelection = useCallback(() => {
    const hash = data[cursor][InternalColumns.COMPANY_HASH]
    if (!hash) return

    viewModel.updateImportData((d) => {
      let result = setMatchedChainByHash(d, hash, null, false)
      result = setMatchedDoorByHash(result, hash, null, false)
      return clearCustomCompanyByHash(result, hash)
    })
  }, [cursor, data, viewModel])

  // Build dropdown options for company types
  const companyTypeOptions = useMemo(() => {
    return (
      viewModel.companySubcategories?.map((subcat) => ({
        label: `${subcat.category_name} / ${subcat.subcategory_name}`,
        value: `${subcat.category_id}-${subcat.value}`,
      })) ?? []
    )
  }, [viewModel.companySubcategories])

  // Get current row's action status
  const currentRow = data[cursor]
  const currentRowHash = currentRow?.[InternalColumns.COMPANY_HASH]
  const isCurrentRowCustom = currentRow?.[InternalColumns.IS_CUSTOM_COMPANY]
  const currentRowCategoryId =
    currentRow?.[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]
  const currentRowSubcategoryId =
    currentRow?.[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]

  // Compute the selected company type value for the dropdown
  const selectedCompanyType = useMemo(() => {
    if (
      isCurrentRowCustom &&
      currentRowCategoryId != null &&
      currentRowSubcategoryId != null
    ) {
      return `${currentRowCategoryId}-${currentRowSubcategoryId}`
    }
    return null
  }, [isCurrentRowCustom, currentRowCategoryId, currentRowSubcategoryId])

  const defaultCompanyType = companyTypeOptions[0]?.value ?? null
  const effectiveCompanyType = selectedCompanyType ?? defaultCompanyType

  // Handler to set current company as custom
  const handleSetCustom = useCallback(
    (value: string | null) => {
      if (!value || !currentRowHash) return

      const [categoryId, subcategoryId] = value.split('-').map(Number)
      viewModel.updateImportData((d) => {
        let result = setAsCustomCompany(
          d,
          currentRowHash,
          categoryId,
          subcategoryId
        )
        result = setMatchedChainByHash(result, currentRowHash, null, false)
        result = setMatchedDoorByHash(result, currentRowHash, null, false)
        return result
      })
    },
    [currentRowHash, viewModel]
  )

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (e.key === 'j') {
        e.preventDefault()
        if (cursor > 0) {
          setCursor(cursor - 1)
        }
      } else if (e.key === 'k') {
        e.preventDefault()
        if (cursor < data.length - 1) {
          setCursor(cursor + 1)
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [cursor, data.length])

  if (!show) {
    return null
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No pending records to review
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Review card */}
      <div className="flex-1 overflow-auto min-h-0">
        <ReviewCard
          csvRow={data[cursor]}
          companyTypeOptions={companyTypeOptions}
          selectedCompanyType={effectiveCompanyType}
          isCustomSelected={!!isCurrentRowCustom}
          onSetCustom={handleSetCustom}
        />
      </div>

      {/* Progress footer */}
      <div className="flex items-center justify-between border-t pt-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="text-lg font-bold text-gray-700">
            {cursor + 1}
            <span className="text-gray-400"> / {data.length}</span>
          </div>
          <div className="h-1.5 w-32 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-fb-dark-green transition-all duration-300"
              style={{ width: `${((cursor + 1) / data.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <FbButton
              type="button"
              onClick={handleClearSelection}
              variant="secondary"
            >
              Clear
            </FbButton>
            <FbButton
              type="button"
              onClick={handlePrev}
              disabled={cursor === 0}
            >
              <Kbd>J</Kbd> ← Prev
            </FbButton>
            <FbButton
              type="button"
              onClick={handleNext}
              disabled={cursor === data.length - 1}
            >
              <Kbd>K</Kbd> Next →
            </FbButton>
          </div>
        </div>
      </div>
    </div>
  )
}

function ReviewCard({
  csvRow,
  companyTypeOptions,
  selectedCompanyType,
  isCustomSelected,
  onSetCustom,
}: {
  csvRow: CSVRow
  companyTypeOptions: Array<{ label: string; value: string }>
  selectedCompanyType: string | null
  isCustomSelected: boolean
  onSetCustom: (value: string | null) => void
}) {
  const viewModel = useImporterViewModel()
  type ReviewTab = 'chain' | 'door'
  const [tab, setTab] = useState<ReviewTab>('chain')

  // Read current selection directly from the row (single source of truth)
  const currentSelectedChain = csvRow[InternalColumns.MATCHED_CHAIN]
  const currentSelectedDoor = csvRow[InternalColumns.MATCHED_DOOR]
  const companyHash = csvRow[InternalColumns.COMPANY_HASH]

  // Sort and limit to top 5 suggestions by score
  const topChains = useMemo(() => {
    const sorted = [...(csvRow.__internal__suggested_chains ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
    // Add hidden placeholders to always have 5 items
    const placeholders = Array(5 - sorted.length).fill(null)
    return [...sorted, ...placeholders]
  }, [csvRow.__internal__suggested_chains])

  const topDoors = useMemo(() => {
    const sorted = [...(csvRow.__internal__suggested_doors ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
    // Add hidden placeholders to always have 5 items
    const placeholders = Array(5 - sorted.length).fill(null)
    return [...sorted, ...placeholders]
  }, [csvRow.__internal__suggested_doors])

  const onSelectChain = useCallback(
    (chain: ChainSearchResult) => {
      // If already selected, deselect it
      if (currentSelectedChain?.item_id === chain.item_id) {
        viewModel.updateImportData((d) => {
          const result = setMatchedChainByHash(d, companyHash, null, false)
          return clearCustomCompanyByHash(result, companyHash)
        })
        return
      }

      // Select this chain
      viewModel.updateImportData((d) => {
        const result = setMatchedChainByHash(d, companyHash, chain, false)
        return clearCustomCompanyByHash(result, companyHash)
      })
    },
    [companyHash, viewModel, currentSelectedChain]
  )

  const onSelectDoor = useCallback(
    (door: DoorSearchResult) => {
      // If already selected, deselect it
      if (currentSelectedDoor?.item_id === door.item_id) {
        viewModel.updateImportData((d) => {
          let result = setMatchedDoorByHash(d, companyHash, null, false)
          result = setMatchedChainByHash(result, companyHash, null, false)
          return clearCustomCompanyByHash(result, companyHash)
        })
        return
      }

      // Select this door (and auto-select matching chain if available)
      const matchingChain =
        csvRow.__internal__suggested_chains?.find(
          (c) => c.item_id === door.chain_id
        ) ?? null

      viewModel.updateImportData((d) => {
        let result = setMatchedDoorByHash(d, companyHash, door, false)
        result = setMatchedChainByHash(
          result,
          companyHash,
          matchingChain,
          false
        )
        return clearCustomCompanyByHash(result, companyHash)
      })
    },
    [
      companyHash,
      csvRow.__internal__suggested_chains,
      viewModel,
      currentSelectedDoor,
    ]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      // Tab switching
      if (e.key === 'u') {
        setTab('chain')
        e.preventDefault()
        return
      }
      if (e.key === 'i') {
        setTab('door')
        e.preventDefault()
        return
      }

      // Selection shortcuts (1-5)
      const num = parseInt(e.key)
      if (num >= 1 && num <= 5) {
        e.preventDefault()
        const index = num - 1

        if (tab === 'chain') {
          const chain = topChains[index]
          if (chain) {
            onSelectChain(chain)
          }
        } else {
          const door = topDoors[index]
          if (door) {
            onSelectDoor(door)
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [tab, topChains, topDoors, onSelectChain, onSelectDoor])

  // Get all mapped company fields (company_match and primary_key types)
  const mappedCompanyFields = useMemo((): MappedField[] => {
    return (
      viewModel.importData?.headers
        .filter((h) => {
          const option = getColumnOption(h.mapped)
          return (
            option?.type === 'company_match' || option?.type === 'primary_key'
          )
        })
        .map((h) => ({
          mapped: h.mapped,
          original: h.original,
          label: getColumnOption(h.mapped)?.name || h.mapped,
        })) || []
    )
  }, [viewModel.importData?.headers])

  // Build input fields dynamically from mapped headers
  const inputFields = useMemo(() => {
    return mappedCompanyFields.map((field) => ({
      label: field.label,
      value: String(csvRow[field.original] ?? '') || '-',
    }))
  }, [mappedCompanyFields, csvRow])

  // Handler for manual search
  const handleManualSearch = useCallback(() => {
    viewModel.setSelectedRowId(csvRow[InternalColumns.ROW_ID])
  }, [viewModel, csvRow])

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Side by side layout: Suggestions on left, Comparison Table on right */}
      <div className="flex-1 flex flex-row gap-4 min-h-0">
        {/* Left: Suggestions Section */}
        <div className="flex-1 flex flex-col min-w-0">
          <Tabs
            tabs={[
              {
                title: (
                  <div className="flex flex-row items-center">
                    <Kbd>U</Kbd> &nbsp; Chains (
                    {Math.min(
                      csvRow.__internal__suggested_chains?.length || 0,
                      5
                    )}
                    )
                  </div>
                ),
                key: 'chain',
              },
              {
                title: (
                  <div className="flex flex-row items-center">
                    <Kbd>I</Kbd> &nbsp; Doors (
                    {Math.min(
                      csvRow.__internal__suggested_doors?.length || 0,
                      5
                    )}
                    )
                  </div>
                ),
                key: 'door',
              },
            ]}
            selectedTab={tab}
            setSelectedTab={setTab}
          />

          <div className="mt-2 flex-1 overflow-auto">
            {tab === 'chain' && (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center gap-2 px-2 py-0.5 mb-1 text-xs font-medium text-gray-600 border-b flex-shrink-0">
                  <div className="flex-shrink-0 w-5"></div>
                  <div className="flex-shrink-0 w-4"></div>
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <div>Name</div>
                    <div>Domain</div>
                  </div>
                </div>
                {/* Cards */}
                <div className="space-y-1 flex-1 overflow-auto">
                  {topChains.map((chain, index) =>
                    chain ? (
                      <SuggestionCard
                        key={chain.item_id}
                        data={chain}
                        isSelected={
                          chain.item_id === currentSelectedChain?.item_id
                        }
                        onSelect={() => onSelectChain(chain)}
                        number={index + 1}
                      />
                    ) : (
                      <div
                        key={`placeholder-chain-${index}`}
                        className="invisible"
                      >
                        <SuggestionCard
                          data={{
                            item_id: '',
                            name: '-',
                            domain: '-',
                            count: 0,
                            type: '',
                            score: 0,
                          }}
                          isSelected={false}
                          onSelect={() => {}}
                          number={index + 1}
                        />
                      </div>
                    )
                  )}
                </div>
                {/* Bottom actions row */}
                <div className="flex items-center gap-2 pt-2 mt-2 border-t flex-shrink-0">
                  <CreateCustomCompanyCard
                    companyTypeOptions={companyTypeOptions}
                    selectedCompanyType={selectedCompanyType}
                    isSelected={isCustomSelected}
                    onSelect={onSetCustom}
                  />
                  <button
                    type="button"
                    onClick={handleManualSearch}
                    className="text-sm text-gray-500 hover:text-gray-700 hover:underline whitespace-nowrap"
                  >
                    Manual Search
                  </button>
                </div>
              </div>
            )}
            {tab === 'door' && (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center gap-2 px-2 py-0.5 mb-1 text-xs font-medium text-gray-600 border-b flex-shrink-0">
                  <div className="flex-shrink-0 w-5"></div>
                  <div className="flex-shrink-0 w-4"></div>
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <div>Name</div>
                    <div>Domain</div>
                    <div>Address</div>
                  </div>
                </div>
                {/* Cards */}
                <div className="space-y-1 flex-1 overflow-auto">
                  {topDoors.map((door, index) =>
                    door ? (
                      <SuggestionCard
                        key={door.item_id}
                        data={door}
                        isSelected={
                          door.item_id === currentSelectedDoor?.item_id
                        }
                        onSelect={() => onSelectDoor(door)}
                        number={index + 1}
                      />
                    ) : (
                      <div
                        key={`placeholder-door-${index}`}
                        className="invisible"
                      >
                        <SuggestionCard
                          data={{
                            item_id: '',
                            name: '-',
                            domain: '-',
                            latitude: 0,
                            longitude: 0,
                            address: '-',
                            city: '',
                            state: '',
                            country: '',
                            zipcode: '',
                            chain_id: '',
                            chain_name: '',
                            chain_size: 0,
                            type: '',
                            score: 0,
                          }}
                          isSelected={false}
                          onSelect={() => {}}
                          number={index + 1}
                        />
                      </div>
                    )
                  )}
                </div>
                {/* Bottom actions row */}
                <div className="flex items-center gap-2 pt-2 mt-2 border-t flex-shrink-0">
                  <CreateCustomCompanyCard
                    companyTypeOptions={companyTypeOptions}
                    selectedCompanyType={selectedCompanyType}
                    isSelected={isCustomSelected}
                    onSelect={onSetCustom}
                  />
                  <button
                    type="button"
                    onClick={handleManualSearch}
                    className="text-sm text-gray-500 hover:text-gray-700 hover:underline whitespace-nowrap"
                  >
                    Manual Search
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Comparison Table */}
        <div className="w-[45%] flex-shrink-0 overflow-auto">
          <ComparisonSummary
            inputFields={inputFields}
            selectedChain={currentSelectedChain}
            selectedDoor={currentSelectedDoor}
            isChainManualMatch={csvRow[InternalColumns.IS_CHAIN_MANUAL_MATCH]}
            isDoorManualMatch={csvRow[InternalColumns.IS_DOOR_MANUAL_MATCH]}
          />
        </div>
      </div>
    </div>
  )
}

function CreateCustomCompanyCard({
  companyTypeOptions,
  selectedCompanyType,
  isSelected,
  onSelect,
}: {
  companyTypeOptions: Array<{ label: string; value: string }>
  selectedCompanyType: string | null
  isSelected: boolean
  onSelect: (value: string | null) => void
}) {
  const canSetCustom = !!selectedCompanyType

  const handleCardClick = useCallback(() => {
    if (isSelected || !canSetCustom || !selectedCompanyType) {
      return
    }
    onSelect(selectedCompanyType)
  }, [isSelected, canSetCustom, onSelect, selectedCompanyType])

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'flex-1 border rounded px-2 py-1 transition-all',
        !isSelected && canSetCustom && 'cursor-pointer',
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-white hover:border-gray-400'
      )}
    >
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-gray-200 text-gray-700 rounded text-xs font-semibold">
          C
        </div>
        <div
          className={cn(
            'text-green-600 font-semibold whitespace-nowrap text-sm',
            {
              invisible: !isSelected,
            }
          )}
        >
          ✓
        </div>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <div className="text-xs font-medium text-gray-900 whitespace-nowrap">
            Create custom:
          </div>
          <Select
            className="flex-1 min-w-0"
            items={companyTypeOptions}
            selected={selectedCompanyType}
            onSelect={onSelect}
            disabled={!isSelected || !canSetCustom}
            nullSelectionLabel="Select type..."
            buttonSize="sm"
          />
        </div>
      </div>
    </div>
  )
}

function SuggestionCard({
  data,
  isSelected,
  onSelect,
  number,
}: {
  data: ChainSearchResult | DoorSearchResult
  isSelected: boolean
  onSelect: () => void
  number?: number
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const hasAddress = 'address' in data && data.address
  const isDoor = 'chain_id' in data
  const chainId = isDoor ? data.chain_id : `${data.type}${data.item_id}`
  const doorId = isDoor ? `${data.type}${data.item_id}` : null

  // Build company URL for the name link
  const companyId = `${data.type}${data.item_id}`
  const companyUrl = getCompanyUrlFromId(companyId)

  return (
    <div
      className={cn(
        'border rounded px-2 py-0.5 cursor-pointer transition-all',
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-white hover:border-gray-400'
      )}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        {number && (
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-gray-200 text-gray-700 rounded text-xs font-semibold">
            {number}
          </div>
        )}
        <div
          className={cn('text-green-600 font-semibold whitespace-nowrap', {
            invisible: !isSelected,
          })}
        >
          ✓
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`grid ${hasAddress ? 'grid-cols-3' : 'grid-cols-2'} gap-2 text-sm`}
          >
            <div className="font-medium text-gray-900 truncate">
              {companyUrl ? (
                <FbLink
                  to={companyUrl}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  {data.name || '-'}
                </FbLink>
              ) : (
                data.name || '-'
              )}
            </div>
            <div className="text-gray-700 truncate">{data.domain || '-'}</div>
            {hasAddress && (
              <div className="text-gray-700 truncate">
                {data.address || '-'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-400">
            {doorId && <span>Door: {doorId}</span>}
            <span>Chain: {chainId}</span>
            <OppTypeBadge typeCode={data.type} size="sm" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ComparisonSummary({
  inputFields,
  selectedChain,
  selectedDoor,
  isChainManualMatch,
  isDoorManualMatch,
}: {
  inputFields: Array<{ label: string; value: string }>
  selectedChain?: ChainSearchResult | null
  selectedDoor?: DoorSearchResult | null
  isChainManualMatch?: boolean
  isDoorManualMatch?: boolean
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const hasChain = !!selectedChain
  const hasDoor = !!selectedDoor

  // Build URLs for selected chain and door
  const chainUrl = selectedChain
    ? getCompanyUrlFromId(`${selectedChain.type}${selectedChain.item_id}`)
    : null
  const doorUrl = selectedDoor
    ? getCompanyUrlFromId(`${selectedDoor.type}${selectedDoor.item_id}`)
    : null

  // Map field labels to chain/door property names
  const fieldToChainDoorKey: Record<string, string> = {
    'company name': 'name',
    'company domain': 'domain',
    'company city': 'city',
    'company state': 'state',
    'company type': 'type',
    'company full address': 'address',
    'company street': 'address',
    'company zip': 'zipcode',
  }

  // Get chain value for a field label
  const getChainValue = (fieldLabel: string): string => {
    if (!selectedChain) return '-'
    const key = fieldToChainDoorKey[fieldLabel.toLowerCase()]
    if (!key) return '-'
    switch (key) {
      case 'name':
        return selectedChain.name || '-'
      case 'type':
        return getOppTypeLabel(selectedChain.type) || '-'
      case 'domain':
        return selectedChain.domain || '-'
      case 'city':
        return (selectedChain as any).city || '-'
      case 'state':
        return (selectedChain as any).state || '-'
      default:
        return '-'
    }
  }

  // Get door value for a field label
  const getDoorValue = (fieldLabel: string): string => {
    if (!selectedDoor) return '-'
    const key = fieldToChainDoorKey[fieldLabel.toLowerCase()]
    if (!key) return '-'
    switch (key) {
      case 'name':
        return selectedDoor.name || '-'
      case 'type':
        return getOppTypeLabel(selectedDoor.type) || '-'
      case 'domain':
        return selectedDoor.domain || '-'
      case 'city':
        return selectedDoor.city || '-'
      case 'state':
        return selectedDoor.state || '-'
      case 'address':
        return selectedDoor.address || '-'
      case 'zipcode':
        return selectedDoor.zipcode || '-'
      default:
        return '-'
    }
  }

  // Check if field is a "name" field for linking
  const isNameField = (fieldLabel: string): boolean => {
    return fieldToChainDoorKey[fieldLabel.toLowerCase()] === 'name'
  }

  return (
    <div className="overflow-hidden rounded border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-2 py-1.5 text-left font-semibold text-gray-700 whitespace-nowrap">
              Field
            </th>
            <th className="px-2 py-1.5 text-left font-semibold text-fb-dark-green border-l border-gray-200 whitespace-nowrap">
              Your Input
            </th>
            <th className="px-2 py-1.5 text-left font-semibold border-l border-gray-200 whitespace-nowrap">
              <span className={hasChain ? 'text-green-700' : 'text-gray-400'}>
                Selected Chain
              </span>
              {isChainManualMatch && (
                <span className="ml-1.5 text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">
                  Manual
                </span>
              )}
            </th>
            <th className="px-2 py-1.5 text-left font-semibold border-l border-gray-200 whitespace-nowrap">
              <span className={hasDoor ? 'text-green-700' : 'text-gray-400'}>
                Selected Door
              </span>
              {isDoorManualMatch && (
                <span className="ml-1.5 text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">
                  Manual
                </span>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {inputFields.map((field, index) => (
            <tr
              key={field.label}
              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
            >
              <td className="px-2 py-1.5 font-medium text-gray-600 border-b border-gray-100 whitespace-nowrap">
                {field.label}
              </td>
              <td className="px-2 py-1.5 text-gray-900 border-l border-gray-200 border-b border-gray-100">
                {field.value || '-'}
              </td>
              <td className="px-2 py-1.5 border-l border-gray-200 border-b border-gray-100">
                {isNameField(field.label) && chainUrl ? (
                  <FbLink to={chainUrl} target="_blank">
                    {getChainValue(field.label)}
                  </FbLink>
                ) : (
                  <span className="text-gray-900">
                    {getChainValue(field.label)}
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 border-l border-gray-200 border-b border-gray-100">
                {isNameField(field.label) && doorUrl ? (
                  <FbLink to={doorUrl} target="_blank">
                    {getDoorValue(field.label)}
                  </FbLink>
                ) : (
                  <span className="text-gray-900">
                    {getDoorValue(field.label)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="text-sm px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-gray-800">
      {children}
    </kbd>
  )
}
